import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { Editor, Range, Element as SlateElement, Node as SlateNode, Transforms } from 'slate'
import { useSlate } from 'slate-react'

import { Button } from 'core-components/library/button'
import {
  insertImageLink,
  insertLink,
} from 'core-components/library/richTextEditor/helpers/slateHelper'
import {
  BLOCK_FORMATS,
  CustomEditor,
  TCustomElement,
  TImageElement,
} from 'core-components/library/richTextEditor/interface/custom-types'
import { TOOLBAR_BUTTONS } from 'core-components/library/richTextEditor/interface/IToolbar'
import { TLink, TLinkView } from 'core-components/library/richTextEditor/interface/Tlink'

import { TOOLBAR_TITLES } from '../../constants'

import { LinkSettingsDialog } from './LinkSettingsDialog'

import styles from './LinkButton.module.scss'

interface IProps {
  disabled: boolean
  type?: TLinkView
  className?: string
}

const emptyLink: TLink = {
  text: '',
  url: '',
  backgroundColor: 'primary',
  openInNewTab: true,
  fontSize: 'base',
  iconPosition: 'start',
}

const getLink = (editor: CustomEditor, type: TLinkView, node?: SlateNode) => {
  const color = type === 'button' ? 'primary' : ''
  if (node) {
    const link: TLink = {
      text: SlateNode.string(node),
      url: node['url'],
      backgroundColor: node['backgroundColor'] || color,
      openInNewTab: node['openInNewTab'],
      fontSize: node['fontSize'],
      iconPosition: node['iconPosition'],
      icon: node['icon'],
    }
    return link
  }

  const { selection } = editor

  if (!selection || (selection && Range.isCollapsed(selection))) {
    return emptyLink
  }
  const selectedNode = editor.getFragment()
  emptyLink.text = SlateNode.string(selectedNode[0])

  return emptyLink
}

export const LinkButton = (props: IProps) => {
  const [linkDialogVisible, setLinkDialogVisible] = useState(false)
  const [link, setLink] = useState<TLink>({
    ...emptyLink,
  })
  const editor = useSlate()

  const [activeElement] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === BLOCK_FORMATS.link,
  })

  const [currentElement] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n),
  })

  const onClose = useCallback(() => {
    setLinkDialogVisible(false)
  }, [])

  //@ts-ignore
  const isActive = (activeElement && activeElement[0]['view'] === props.type) || false

  const linkType = useMemo(() => {
    if (!props.type) {
      if (!currentElement) {
        return 'button'
      }
      const newType =
        (currentElement[0] as TCustomElement).type === BLOCK_FORMATS.image ? 'image' : 'button'

      return newType
    }

    return props.type
  }, [currentElement, props.type])

  return (
    <div>
      <Button
        onClick={(e) => {
          e.preventDefault()
          if (activeElement) {
            setLink(getLink(editor, linkType, activeElement[0]))
          } else {
            setLink(getLink(editor, linkType))
          }
          setLinkDialogVisible(true)
        }}
        className={classNames(styles.root, props.className || '')}
        title={
          linkType === 'button' || linkType === 'image'
            ? TOOLBAR_TITLES[TOOLBAR_BUTTONS.LinkButton]
            : TOOLBAR_TITLES[TOOLBAR_BUTTONS.Link]
        }
        active={isActive}
        variant={'text'}
        color={'grey'}
        iconButton={
          linkType === 'button' || linkType === 'image'
            ? 'kbase kbase-editor-add-button'
            : 'kbase kbase-link'
        }
        disabled={props.disabled}
      />
      <LinkSettingsDialog
        onCancel={onClose}
        type={linkType}
        onSave={(newLink) => {
          if (activeElement) {
            Transforms.select(editor, activeElement[1])
          }
          if (linkType === 'image') {
            insertImageLink(editor, newLink)
          } else {
            insertLink(editor, newLink, linkType)
          }
          setLinkDialogVisible(false)
        }}
        src={(currentElement && (currentElement[0] as TImageElement).url) || ''}
        link={link}
        onClose={onClose}
        show={linkDialogVisible}
      />
    </div>
  )
}
